import type {
  FsrsCard,
  FsrsCardReviewLog,
} from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import onetime from "@-/com.npmjs.onetime";
import {
  type Card,
  forgetting_curve,
  FSRS6_DEFAULT_DECAY,
  fsrs,
  type Grade,
  Rating,
  type ReviewLog,
  State,
} from "@-/com.npmjs.ts-fsrs";

export type ReviewRating = Grade;

export {
  default_request_retention as FSRS_RETENTION_TARGET,
  FSRS6_DEFAULT_DECAY as FSRS_DECAY,
} from "@-/com.npmjs.ts-fsrs";

export const REVIEW_RATING = {
  Again: Rating.Again,
  Easy: Rating.Easy,
  Good: Rating.Good,
  Hard: Rating.Hard,
} satisfies Record<"Again" | "Easy" | "Good" | "Hard", ReviewRating>;

export const FSRS_STATE = {
  Learning: State.Learning,
  New: State.New,
  Relearning: State.Relearning,
  Review: State.Review,
} satisfies Record<"Learning" | "New" | "Relearning" | "Review", State>;

export const FSRS_STATE_LABEL: Record<number, string> = {
  [State.Learning]: "Learning",
  [State.New]: "New",
  [State.Relearning]: "Relearning",
  [State.Review]: "Review",
};

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;

export const MINIMUM_REVIEW_INTERVAL_MS = 5 * MINUTE_MS;

// A card's FIRST review is taken at a stopping point at least five minutes after the card was
// introduced. ts-fsrs has no notion of "how long ago was I told the answer", so it reads that
// recall exactly as it would one taken a day later and hands out its full interval (8 days for
// Easy). A pass minutes after being shown the answer proves far less than that, so the first
// review's DUE DATE is overridden here to a schedule we choose.
//
// Only `due`/`scheduledDays` are overridden — never `stability`. Stability is the memory model,
// not the schedule: it stays exactly as ts-fsrs computed it, so the second review still scores
// against an honest estimate. Pulling the review earlier than that estimate is self-correcting,
// because ts-fsrs sees "recalled well before due" and grants a proportionally smaller increase.
const FIRST_FRESH_RATING_INTERVAL_MS = {
  [REVIEW_RATING.Again]: 10 * MINUTE_MS,
  [REVIEW_RATING.Easy]: DAY_MS,
  [REVIEW_RATING.Good]: 8 * HOUR_MS,
  [REVIEW_RATING.Hard]: HOUR_MS,
} satisfies Partial<Record<ReviewRating, number>>;

// Target recall probability at the due date. Raised above the ts-fsrs default of 0.9 to damp
// interval growth: at 0.9 a card rated Easy repeatedly runs 1d → 18d → 130d → 713d, jumps large
// enough to read as broken. At 0.95 the same path is 1d → 7d → 29d → 99d, which lines up with
// the user-facing mastery bands (see `lib/cards/user-card/mastery`). The cost is roughly one
// extra review per card per two years.
const FSRS_REQUEST_RETENTION = 0.95;

export const getFsrsScheduler = onetime(() =>
  fsrs({ enable_fuzz: true, request_retention: FSRS_REQUEST_RETENTION }),
);

function isFreshFsrsCard(row: FsrsCard): boolean {
  return row.reps === 0 && row.lastReview === null;
}

function getFirstFreshRatingIntervalMs(rating: ReviewRating): number | undefined {
  return FIRST_FRESH_RATING_INTERVAL_MS[rating as keyof typeof FIRST_FRESH_RATING_INTERVAL_MS];
}

/**
 * Apply the first-review schedule override to the OUTGOING card.
 *
 * Only `due`/`scheduledDays` are touched. Two things are deliberately left alone:
 *
 * - `stability`, because it is the memory model rather than the schedule. Overwriting it with
 *   the interval (the old behaviour) discarded a real estimate and made every later interval
 *   compound off a fabricated one.
 * - the review log, because its `due`/`scheduledDays`/`stability` describe the card as it stood
 *   BEFORE this review — that is precisely what {@link rollbackReview} restores from. Writing
 *   the outgoing schedule into those columns corrupted undo.
 */
function applyFirstFreshRatingInterval(
  row: FsrsCard,
  rating: ReviewRating,
  now: Date,
  nextCard: FsrsCardPatch,
): void {
  const intervalMs = getFirstFreshRatingIntervalMs(rating);
  if (intervalMs === undefined || !isFreshFsrsCard(row)) {
    return;
  }
  nextCard.due = new Date(now.getTime() + intervalMs).toISOString();
  // `scheduledDays` must track the overridden `due` or the row contradicts itself — it is the
  // interval the user-facing mastery bands read.
  nextCard.scheduledDays = Math.round(intervalMs / DAY_MS);
}

function dbStateToFsrsState(state: number): State {
  switch (state) {
    case State.New:
    case State.Learning:
    case State.Review:
    case State.Relearning: {
      return state;
    }
    default: {
      return State.New;
    }
  }
}

/**
 * Widen a stored rating ordinal back to the library's `Rating`.
 *
 * The column is a plain integer, so — exactly as with {@link dbStateToFsrsState} — an unknown
 * value has to resolve to something rather than crash a rollback. `Manual` is the honest
 * fallback: it is the one rating that carries no grade, and `rollback` rejects it explicitly
 * instead of silently rewinding the card with a grade the log never recorded.
 */
function dbRatingToFsrsRating(rating: number): Rating {
  switch (rating) {
    case Rating.Again:
    case Rating.Hard:
    case Rating.Good:
    case Rating.Easy: {
      return rating;
    }
    default: {
      return Rating.Manual;
    }
  }
}

export function dbCardToFsrsCard(row: FsrsCard): Card {
  return {
    difficulty: row.difficulty,
    due: new Date(row.due),
    elapsed_days: row.elapsedDays,
    lapses: row.lapses,
    last_review: row.lastReview === null ? undefined : new Date(row.lastReview),
    learning_steps: row.learningSteps,
    reps: row.reps,
    scheduled_days: row.scheduledDays,
    stability: row.stability,
    state: dbStateToFsrsState(row.state),
  };
}

type FsrsCardPatch = Pick<
  FsrsCard,
  | "difficulty"
  | "due"
  | "elapsedDays"
  | "lapses"
  | "lastReview"
  | "learningSteps"
  | "reps"
  | "scheduledDays"
  | "stability"
  | "state"
>;

export function buildFsrsCardPatch(card: Card): FsrsCardPatch {
  return {
    difficulty: card.difficulty,
    due: card.due.toISOString(),
    elapsedDays: card.elapsed_days,
    lapses: card.lapses,
    lastReview: card.last_review === undefined ? null : card.last_review.toISOString(),
    learningSteps: card.learning_steps,
    reps: card.reps,
    scheduledDays: card.scheduled_days,
    stability: card.stability,
    state: card.state,
  };
}

type FsrsReviewLogInsert = Pick<
  FsrsCardReviewLog,
  | "difficulty"
  | "due"
  | "elapsedDays"
  | "lastElapsedDays"
  | "learningSteps"
  | "rating"
  | "review"
  | "scheduledDays"
  | "stability"
  | "state"
>;

export function buildFsrsReviewLogInsert(log: ReviewLog): FsrsReviewLogInsert {
  return {
    difficulty: log.difficulty,
    due: log.due.toISOString(),
    elapsedDays: log.elapsed_days,
    lastElapsedDays: log.last_elapsed_days,
    learningSteps: log.learning_steps,
    rating: log.rating,
    review: log.review.toISOString(),
    scheduledDays: log.scheduled_days,
    stability: log.stability,
    state: log.state,
  };
}

export function scheduleReview(
  row: FsrsCard,
  rating: ReviewRating,
  now: Date,
): { nextCard: FsrsCardPatch; reviewLog: FsrsReviewLogInsert } {
  const result = getFsrsScheduler().next(dbCardToFsrsCard(row), now, rating);
  const nextCard = buildFsrsCardPatch(result.card);
  const reviewLog = buildFsrsReviewLogInsert(result.log);
  applyFirstFreshRatingInterval(row, rating, now, nextCard);
  const earliestDue = now.getTime() + MINIMUM_REVIEW_INTERVAL_MS;
  if (Date.parse(nextCard.due) < earliestDue) {
    nextCard.due = new Date(earliestDue).toISOString();
    nextCard.scheduledDays = 0;
  }
  return { nextCard, reviewLog };
}

/**
 * Rebuild the card as it stood BEFORE the review recorded by `log`.
 *
 * The inverse of {@link scheduleReview}, delegated to `ts-fsrs`'s own `rollback` rather than
 * hand-restoring columns. That matters because the review log does NOT store every field it
 * takes to undo a review: `reps`, `lapses` and `lastReview` have no pre-review columns. The
 * library derives them from the log's own `state`/`rating`/`due` (`reps - 1`; `lapses - 1` only
 * for an `Again` rated out of `Review`; `lastReview` from the log's `due`, which is the previous
 * scheduling date). Restoring the logged columns alone would leave `reps`/`lapses` permanently
 * inflated and `lastReview` pinned to a review that no longer exists.
 *
 * `applyFirstFreshRatingInterval` needs no inverse: it overwrites `due`/`scheduledDays` on the
 * OUTGOING card only, never on the review log, whose columns describe the card as it stood
 * before the review. The fresh-card branch below is therefore belt-and-braces — `rollback`
 * already reproduces the never-reviewed state — but it is kept so an undo still lands on a
 * sane, immediately-due card if a row written by the older code (which DID pollute the log's
 * `due`/`scheduledDays`/`stability` with post-review values) is rolled back.
 */
export function rollbackReview(
  row: FsrsCard,
  log: FsrsCardReviewLog,
  undoneAt: Date,
): FsrsCardPatch {
  const previousCard = buildFsrsCardPatch(
    getFsrsScheduler().rollback(dbCardToFsrsCard(row), {
      difficulty: log.difficulty,
      due: new Date(log.due),
      elapsed_days: log.elapsedDays,
      last_elapsed_days: log.lastElapsedDays,
      learning_steps: log.learningSteps,
      rating: dbRatingToFsrsRating(log.rating),
      review: new Date(log.review),
      scheduled_days: log.scheduledDays,
      stability: log.stability,
      state: dbStateToFsrsState(log.state),
    }),
  );

  if (previousCard.reps > 0) {
    return previousCard;
  }

  // Undoing a fresh card's first review. All three columns the log carries for `due`,
  // `scheduledDays` and `stability` describe the schedule that rating PRODUCED, so none of them
  // can be restored from. A never-reviewed card is simply due — `due` is set by the caller to
  // the moment of the undo, which puts the card back at the front of the queue where an
  // unreviewed card belongs, and `isFreshFsrsCard` reads true again so the next rating gets the
  // first-introduction intervals rather than a compounded schedule.
  return {
    ...previousCard,
    due: undoneAt.toISOString(),
    lastReview: null,
    scheduledDays: 0,
    stability: 0,
  };
}

export function getCardRetrievability(row: FsrsCard, now: Date): number {
  return getFsrsScheduler().get_retrievability(dbCardToFsrsCard(row), now, false);
}

export type ForgettingCurveSample = {
  dateMs: number;
  daysFromLastReview: number;
  retrievability: number;
};

/**
 * Sample the FSRS forgetting curve R(t) = (1 + factor * t / (9 * S))^decay starting from
 * `lastReview`. Returns null when stability isn't established (new card with no review history).
 */
export function forgettingCurveSamples(
  row: FsrsCard,
  options: { minSpanDays?: number; sampleCount?: number } = {},
): { lastReviewMs: number; samples: ForgettingCurveSample[] } | null {
  if (row.lastReview === null || row.stability <= 0) {
    return null;
  }
  const minSpanDays = options.minSpanDays ?? 1;
  const sampleCount = options.sampleCount ?? 48;
  const lastReviewMs = new Date(row.lastReview).getTime();
  const spanDays = Math.max(minSpanDays, row.stability * 4);
  const samples: ForgettingCurveSample[] = [];
  for (let i = 0; i < sampleCount; i += 1) {
    const t = (i / (sampleCount - 1)) * spanDays;
    samples.push({
      dateMs: lastReviewMs + t * DAY_MS,
      daysFromLastReview: t,
      retrievability: forgetting_curve(FSRS6_DEFAULT_DECAY, t, row.stability),
    });
  }
  return { lastReviewMs, samples };
}

export function previewIntervals(
  row: FsrsCard,
  now: Date,
): Record<ReviewRating, { dueMs: number; scheduledDays: number }> {
  const preview = getFsrsScheduler().repeat(dbCardToFsrsCard(row), now);
  const nowMs = now.getTime();
  const freshCard = isFreshFsrsCard(row);
  const previewDueMs = (rating: ReviewRating) => {
    const intervalMs = freshCard ? getFirstFreshRatingIntervalMs(rating) : undefined;
    if (intervalMs !== undefined) {
      return intervalMs;
    }
    return Math.max(MINIMUM_REVIEW_INTERVAL_MS, preview[rating].card.due.getTime() - nowMs);
  };
  const previewScheduledDays = (rating: ReviewRating) => {
    const intervalMs = freshCard ? getFirstFreshRatingIntervalMs(rating) : undefined;
    return intervalMs === undefined
      ? preview[rating].card.scheduled_days
      : Math.round(intervalMs / DAY_MS);
  };
  return {
    [Rating.Again]: {
      dueMs: previewDueMs(Rating.Again),
      scheduledDays: previewScheduledDays(Rating.Again),
    },
    [Rating.Easy]: {
      dueMs: previewDueMs(Rating.Easy),
      scheduledDays: previewScheduledDays(Rating.Easy),
    },
    [Rating.Good]: {
      dueMs: previewDueMs(Rating.Good),
      scheduledDays: previewScheduledDays(Rating.Good),
    },
    [Rating.Hard]: {
      dueMs: previewDueMs(Rating.Hard),
      scheduledDays: previewScheduledDays(Rating.Hard),
    },
  };
}
